import { useAtom } from '@reatom/npm-react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, EditIcon, ResetIcon } from '@blade/ui-kit'
import { Socials } from '@root/entities/socials'
import { CurrencyBanner } from '@root/features/currency/ui/currency-banner/currency-banner'
import { CurrentPremiumBannerFooter } from '@root/features/currency/ui/currency-banner/current-premium-banner-footer'

import type { PairModel } from '@root/entities/pair'

type Props = {
  pair: PairModel
  imagePreview: string | undefined
  onUploadClick: () => void
  onRevertClick?: () => void
}

export const PremiumBannerDrawerPreview: React.FC<Props> = ({
  pair,
  imagePreview,
  onUploadClick,
  onRevertClick,
}) => {
  const { t } = useTranslation()
  const [socials] = useAtom(pair.quote.socials)
  const [premium] = useAtom(pair.premiumInfo.premium)
  const [owner] = useAtom(pair.premiumInfo.owner)

  return (
    <Root>
      <SubTitle>{t('premium.image-upload.size', { size: '3:1' })} </SubTitle>
      <Preview>
        <CurrencyBanner
          premium={false}
          socials={Socials.empty()}
          banner={imagePreview}
          owner={undefined}
          pair={pair}
          archiveButtonSlot={undefined}
          footerSlot={undefined}
        />
        <Shadow>
          <ButtonOverlay
            type="button"
            iconLeft={<EditIcon />}
            size="xs"
            onClick={onUploadClick}
          />
          {onRevertClick ? (
            <ButtonOverlay
              type="button"
              iconLeft={<ResetIcon />}
              size="xs"
              onClick={onRevertClick}
            />
          ) : undefined}
        </Shadow>
      </Preview>
      <Caption>{t('premium.update-logo.preview')}</Caption>
      <CurrencyBanner
        premium={premium}
        socials={socials}
        banner={imagePreview}
        owner={owner}
        pair={pair}
        archiveButtonSlot={undefined}
        footerSlot={<CurrentPremiumBannerFooter premium={pair.premiumInfo} />}
      />
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  justify-items: center;
`

const Preview = styled.div`
  display: grid;
  position: relative;
  width: min-content;
`

const SubTitle = styled.p`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 500;

  margin: 0 0 10px;
  justify-self: center;
  text-align: center;
`

const Caption = styled.p`
  color: var(--text-12);

  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  margin: 16px 0;
  justify-self: center;
  text-align: center;
`

const Shadow = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  padding: 8px;

  position: absolute;
  inset: 0;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const ButtonOverlay = styled(Button)`
  color: var(--text-12);

  border: 1px solid var(--background-5);

  border-radius: 64px;
  backdrop-filter: blur(30px);
  background: var(--background-3);

  &:hover {
    background: var(--background-3);
  }
`
